import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {SharedModule} from "../../../shared/shared.module";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {ButtonIconComponent} from "../button-icon/button-icon.component";
import {RouterLink} from "@angular/router";
import {PageResponse} from "../../../models/page-response";
import {LoaderComponent} from "../loader/loader.component";
import {CheckboxComponent} from "../checkbox/checkbox.component";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Subscription} from "rxjs";

export interface Column {
  name: string;
  label: string;
  suffix?: string;
  color?: boolean;
  date?: boolean;
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    SharedModule,
    NgForOf,
    ButtonIconComponent,
    NgIf,
    RouterLink,
    LoaderComponent,
    DatePipe,
    CheckboxComponent,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})
export class TableComponent implements OnInit, OnDestroy {
  @Input() public columns: Column[] = [];
  @Input() public selectable = false;
  @Input() public identifier = 'id';
  @Input() public hasDelete = false;
  private data?: PageResponse<any> | any[] | null;

  @Input('data')
  set dataSetter(value: PageResponse<any> | any[] | null) {
    this.data = value;

    if (value) {
      this.createSelectionFormControls();
    }
  }
  @Output() public pageChange = new EventEmitter<number>();
  @Output() public delete = new EventEmitter<any>();
  @Output() public selectionChange = new EventEmitter<number[]>();
  public selectAllControl = new FormControl(false);
  public selected: number[] = [];
  public selectionFormControls = new Map<number, FormControl>();
  private subscriptions: Subscription[] = [];

  public ngOnInit() {
    this.subscriptions.push(
      this.selectAllControl.valueChanges.subscribe(value => {
        for (const control of this.selectionFormControls.values()) {
          control.setValue(value, { emitEvent: false });
        }

        this.checkSelection();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get pageResponse(): PageResponse<any> {
    return this.data as PageResponse<any>;
  }

  get isPageResponse(): boolean {
    return (this.data && 'totalElements' in this.data) ?? false;
  }

  get list(): any[] {
    return (this.isPageResponse ? this.pageResponse.content : this.data) as any[];
  }

  nextPage() {
    if (this.pageResponse.last) {
      return;
    }

    this.pageChange.emit(this.pageResponse.number + 1);
  }

  previousPage() {
    if (this.pageResponse.first) {
      return;
    }

    this.pageChange.emit(this.pageResponse.number - 1);
  }

  extractProperty(object: any, keyWithDot: string): any {
    return keyWithDot.split('.').reduce((o, i) => (o && o[i] !== undefined) ? o[i] : null, object);
  }

  createSelectionFormControls() {
    // Create a form control for each item in the list
    this.selectionFormControls.clear();

    for (const item of this.list) {
      if (!this.selectionFormControls.has(item[this.identifier])) {
        const control = new FormControl(false);
        this.selectionFormControls.set(item[this.identifier], control);

        this.subscriptions.push(
          control.valueChanges.subscribe(() => {
            this.checkSelection();
          }),
        );
      }
    }
  }

  getSelectionControlForItem(item: any): FormControl {
    return this.selectionFormControls.get(item[this.identifier])!;
  }

  checkSelection() {
    this.selected = Array.from(this.selectionFormControls.entries())
      .filter(([_, control]) => control.value)
      .map(([id, _]) => id);

    // Now check if this is all selected
    this.selectAllControl.setValue(this.selected.length === this.list.length, { emitEvent: false });

    this.selectionChange.emit(this.selected);
  }
}
